import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Hammer, UserCheck, Award, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Breadcrumbs } from '../../components/Breadcrumbs';
import { useSEO } from '../../hooks/useSEO';
// Images
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/pPR23bNLSAVudMWhF2ynaG/_DSC8241.webp';
const DETAIL_IMAGE_1 = 'https://cdn.magicpatterns.com/uploads/pKPzBNwMUWtZUzQjDCs91d/6842e88f2f523e985c5fa1a4__DSC8349-2.jpg';
const DETAIL_IMAGE_2 = 'https://cdn.magicpatterns.com/uploads/3xL1jkjmSyiggyKFQmbzxH/_DSC8261.webp';
export function CraftsmanshipPage() {
  useSEO({
    title: 'Signature Craftsmanship — One Van, One Craftsman',
    description: 'Robeta\'s "One Van, One Craftsman" philosophy ensures personal accountability and flawless fit and finish in every hand-built camper van.'
  });
  return <main className="bg-white min-h-screen" data-id="element-876">
      {/* 1. FULL-SCREEN HERO */}
      <section className="relative min-h-screen w-full flex flex-col justify-between overflow-hidden" data-id="element-877">
        <div className="absolute inset-0 z-0" data-id="element-878">
          <img src={HERO_IMAGE} alt="Craftsmanship Hero" className="w-full h-full object-cover" data-id="element-879" />
          <div className="absolute inset-0 bg-black/50" data-id="element-880" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/20" data-id="element-881" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pt-32" data-id="element-882">
          <Breadcrumbs items={[{
          label: 'Home',
          href: '/'
        }, {
          label: 'Robeta Difference',
          href: '/robeta-difference'
        }, {
          label: 'Signature Craftsmanship'
        }]} variant="light" data-id="element-883" />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10 pb-24 md:pb-32" data-id="element-884">
          <motion.div initial={{
          opacity: 0,
          y: 40
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="max-w-4xl" data-id="element-885">
            <span className="text-robeta-copper text-sm font-bold uppercase tracking-wide-editorial block mb-6" data-id="element-886">
              One Van, One Craftsman
            </span>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-white uppercase tracking-editorial leading-[0.9] mb-8" data-id="element-887">
              Signature
              <br data-id="element-888" />
              Craftsmanship
            </h1>
            <p className="text-xl md:text-2xl font-light text-white/80 max-w-2xl leading-relaxed" data-id="element-889">
              Quality cannot be automated. It must be built by hand.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. INTRO SECTION */}
      <section className="py-24 md:py-32 bg-white" data-id="element-890">
        <div className="container mx-auto px-6 md:px-12" data-id="element-891">
          <div className="max-w-4xl mx-auto text-center" data-id="element-892">
            <h2 className="text-3xl md:text-5xl font-light text-robeta-black leading-tight mb-8" data-id="element-893">
              "We rejected the assembly line. We chose{' '}
              <span className="italic font-serif text-robeta-copper" data-id="element-894">
                accountability
              </span>{' '}
              instead."
            </h2>
            <p className="text-lg text-robeta-black/60 font-light leading-relaxed max-w-2xl mx-auto" data-id="element-895">
              In most factories, a van moves down a line where workers perform
              the same repetitive task all day. At Robeta, we do things
              differently. We employ a "One Van, One Craftsman" philosophy. A
              dedicated master carpenter (or a small, tight-knit team) takes
              ownership of your vehicle from the moment the insulation is
              applied until the final cabinet is polished.
            </p>
          </div>
        </div>
      </section>

      {/* 3. DEEP DIVE 1: THE HUMAN TOUCH */}
      <section className="py-20 md:py-32 bg-robeta-mist/10" data-id="element-896">
        <div className="container mx-auto px-6 md:px-12" data-id="element-897">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-898">
            <div className="order-2 lg:order-1" data-id="element-899">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-900">
                Personal Pride
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-901">
                The Human Touch
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-902">
                <p data-id="element-903">
                  When a single craftsman builds your interior, they know every
                  screw, every wire, and every joint. They take personal pride
                  in the fit and finish. If a panel is a millimeter off, they
                  don't pass it down the line—they fix it.
                </p>
                <p data-id="element-904">
                  This accountability creates a level of quality that mass
                  production simply cannot match. It fosters a culture of
                  excellence where "good enough" is never acceptable.
                </p>
                <p data-id="element-905">
                  Your van isn't just a unit number to us. It's a testament to
                  the skill and dedication of the person who built it.
                </p>
              </div>
            </div>
            <div className="order-1 lg:order-2 relative aspect-[4/3] overflow-hidden" data-id="element-906">
              <img src={DETAIL_IMAGE_1} alt="Craftsman Detail Work" className="absolute inset-0 w-full h-full object-cover" data-id="element-907" />
            </div>
          </div>
        </div>
      </section>

      {/* 4. DEEP DIVE 2: SLOVENIAN HERITAGE */}
      <section className="py-20 md:py-32 bg-white" data-id="element-908">
        <div className="container mx-auto px-6 md:px-12" data-id="element-909">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-910">
            <div className="relative aspect-[4/3] overflow-hidden" data-id="element-911">
              <img src={DETAIL_IMAGE_2} alt="Finished Interior" className="absolute inset-0 w-full h-full object-cover" data-id="element-912" />
            </div>
            <div data-id="element-913">
              <span className="text-robeta-copper text-xs font-bold uppercase tracking-wide-editorial block mb-4" data-id="element-914">
                Tradition & Tech
              </span>
              <h3 className="text-3xl md:text-4xl font-bold text-robeta-black uppercase tracking-editorial mb-6" data-id="element-915">
                Slovenian Heritage
              </h3>
              <div className="space-y-6 text-robeta-black/70 font-light leading-relaxed" data-id="element-916">
                <p data-id="element-917">
                  Slovenia is the heart of Europe's caravanning industry, with a
                  woodworking tradition that spans centuries. Robeta combines
                  this old-world heritage with modern CNC precision.
                </p>
                <p data-id="element-918">
                  We use state-of-the-art machinery to cut our Air Furniture
                  panels to within a tenth of a millimeter. But the assembly,
                  the stitching, and the final adjustments are always done by
                  human hands.
                </p>
                <p data-id="element-919">
                  It is this fusion of technology and tradition that defines the
                  Robeta standard.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. KEY BENEFITS GRID */}
      <section className="py-24 bg-robeta-black text-white" data-id="element-920">
        <div className="container mx-auto px-6 md:px-12" data-id="element-921">
          <div className="text-center mb-16" data-id="element-922">
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-editorial mb-4" data-id="element-923">
              Built with Pride
            </h2>
            <div className="w-24 h-1 bg-robeta-copper mx-auto" data-id="element-924" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-925">
            {[{
            icon: UserCheck,
            title: 'Personal Accountability',
            desc: 'One craftsman oversees your build from start to finish.'
          }, {
            icon: Hammer,
            title: 'Hand-Assembled',
            desc: 'Every component is fitted by hand for perfect alignment.'
          }, {
            icon: Award,
            title: 'Quality Control',
            desc: 'Rigorous multi-point inspection before any vehicle leaves the factory.'
          }, {
            icon: HeartHandshake,
            title: 'Passion',
            desc: 'Built by people who love what they do and care about your journey.'
          }].map((item, i) => <div key={i} className="bg-white/5 p-8 border border-white/10 hover:border-robeta-copper/50 transition-colors" data-id="element-926">
                <item.icon className="w-10 h-10 text-robeta-copper mb-6" data-id="element-927" />
                <h3 className="text-xl font-bold uppercase tracking-wide mb-4" data-id="element-928">
                  {item.title}
                </h3>
                <p className="text-white/60 font-light leading-relaxed text-sm" data-id="element-929">
                  {item.desc}
                </p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. NEXT PILLAR NAV */}
      <section className="border-t border-robeta-black/10 bg-white" data-id="element-930">
        <div className="container mx-auto px-6 md:px-12 py-16 md:py-24" data-id="element-931">
          <div className="flex flex-col items-center text-center" data-id="element-932">
            <span className="text-robeta-black/40 text-xs font-bold uppercase tracking-widest mb-4" data-id="element-933">
              Next Pillar
            </span>
            <Link to="/robeta-difference/silent-travel" className="group flex flex-col items-center" data-id="element-934">
              <h2 className="text-4xl md:text-6xl font-bold text-robeta-black uppercase tracking-editorial mb-6 group-hover:text-robeta-copper transition-colors" data-id="element-935">
                Experience Silent Travel
              </h2>
              <div className="flex items-center text-sm font-bold uppercase tracking-widest text-robeta-black group-hover:text-robeta-copper transition-colors" data-id="element-936">
                Complete the Circle <ArrowRight className="ml-2 w-4 h-4" data-id="element-937" />
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 7. CTA SECTION */}
      <section className="py-28 md:py-36 bg-robeta-mist/20 text-center" data-id="element-938">
        <div className="container mx-auto px-6 md:px-12" data-id="element-939">
          <h2 className="text-4xl md:text-5xl font-bold text-robeta-black uppercase tracking-editorial mb-8" data-id="element-940">
            Meet the Masterpiece
          </h2>
          <p className="text-lg text-robeta-black/60 font-light max-w-2xl mx-auto mb-12" data-id="element-941">
            See the difference true craftsmanship makes at your nearest dealer.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-6" data-id="element-942">
            <Link to="/dealer-network" className="px-10 py-4 bg-robeta-black text-white font-bold uppercase tracking-widest hover:bg-robeta-copper transition-colors min-w-[200px]" data-id="element-943">
              Find a Dealer
            </Link>
            <Link to="/models" className="px-10 py-4 border border-robeta-black text-robeta-black font-bold uppercase tracking-widest hover:bg-white transition-colors min-w-[200px]" data-id="element-944">
              View Models
            </Link>
          </div>
        </div>
      </section>
    </main>;
}